import { LayoutTemplate, Plus } from 'lucide-react';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import supabase from 'modules/core/supabase/supabase';
import MediaManagerTemplateList from 'modules/manager/ui/components/media-manager-template-list';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import UDButton from 'ud-ui/components/button';
import UDHeaderTitle from 'ud-ui/components/header-title';
import { useMainLayout } from 'ud-ui/layout/main/context';
import useCreateTemplateModal from '../hooks/modals/use-create-template-modal';
import useDeleteTemplateModal from '../hooks/modals/use-delete-template-modal';
import useEditTemplateModal from '../hooks/modals/use-edit-template-modal';

/** How long a freshly duplicated template stays highlighted in the list. */
const HIGHLIGHT_DURATION_MS = 4000;

/** Router state accepted by this page — see the duplicate flow on the presets page. */
interface TemplatesListLocationState {
  highlightTemplateId?: number;
}

const MediaManagerTemplatesListPage: React.FC = () => {
  const { setHeaderContent } = useMainLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const tournamentId = useSelector(authStoreUserTournamentId);

  const [templates, setTemplates] = useState<MediaManagerTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [highlightedId, setHighlightedId] = useState<number | null>(null);

  const requestedHighlightId = (location.state as TemplatesListLocationState | null)?.highlightTemplateId ?? null;

  useEffect(() => {
    if (requestedHighlightId == null) return;
    setHighlightedId(requestedHighlightId);
    // Consume the router state so a reload or a back-navigation does not flash the row again.
    navigate(Pages.MEDIA_MANAGER.INDEX, { replace: true, state: null });
  }, [requestedHighlightId, navigate]);

  useEffect(() => {
    if (highlightedId == null) return;
    const timer = setTimeout(() => setHighlightedId(null), HIGHLIGHT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [highlightedId]);

  const openTemplate = useCallback(
    (template: MediaManagerTemplate) => {
      navigate(Pages.MEDIA_MANAGER.builders.editor(template.id));
    },
    [navigate],
  );

  useEffect(() => {
    setHeaderContent(<UDHeaderTitle>Медиа менеджер</UDHeaderTitle>);
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const loadTemplates = useCallback(() => {
    if (!tournamentId) return;
    setIsLoading(true);
    supabase.mediaManager
      .getTemplates({ tournamentId })
      .then(setTemplates)
      .catch((error) => {
        console.error('Failed to load media manager templates:', error);
      })
      .finally(() => setIsLoading(false));
  }, [tournamentId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const existingNames = useMemo(() => templates.map((t) => t.name.trim()), [templates]);
  const { modal: createModal, open: openCreateModal } = useCreateTemplateModal({
    tournamentId: tournamentId ?? null,
    existingNames,
    templatesCount: templates.length,
    onCreated: loadTemplates,
  });
  const { modal: editModal, open: openEditModal } = useEditTemplateModal({
    templates,
    onUpdated: loadTemplates,
  });
  const { modal: deleteModal, open: openDeleteModal } = useDeleteTemplateModal({
    onDeleted: loadTemplates,
  });

  return (
    <div className="flex h-full flex-col p-6">
      <div className="mb-4 flex justify-end gap-3">
        <UDButton variant="secondary" className="flex items-center" onClick={() => navigate(Pages.MEDIA_MANAGER.TEMPLATES_LIBRARY)}>
          <LayoutTemplate size={16} className="mr-2" />
          Общие шаблоны
        </UDButton>
        <UDButton variant="dark" className="flex items-center" onClick={openCreateModal}>
          <Plus size={16} className="mr-2" />
          Добавить шаблон
        </UDButton>
      </div>

      <MediaManagerTemplateList
        templates={templates}
        isLoading={isLoading}
        highlightedTemplateId={highlightedId}
        onCreate={openCreateModal}
        onSelect={openTemplate}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {createModal}
      {editModal}
      {deleteModal}
    </div>
  );
};

export default MediaManagerTemplatesListPage;
